import type { Components, Theme } from "@mui/material";

declare module "@mui/material/Typography" {
	interface TypographyPropsVariantOverrides {
		// sizes
		body3: true;
		bodyMedium: true;
		bodyLarge: true;
		bodyXLarge: true;

		// docs
		docs: true;
		docTitle1: true;
		docTitle2: true;

		// onboarding
		onboardingTitle1: true;
		onboardingTitle2: true;
		onboardingLabel: true;

		// sections
		website: true;
		dashboard: true;

		// components
		alertTitle: true;
		card: true;
	}
}

export const MuiTypography: Components<Theme>["MuiTypography"] = {
	styleOverrides: {
		root: {
			strong: {
				fontWeight: 600,
			},
		},
	},
	variants: [
		{
			props: { component: "h1" },
			style: { fontWeight: 600, lineHeight: 1.2 },
		},
		{
			props: { component: "h2" },
			style: { fontWeight: 600, lineHeight: 1.2 },
		},
		{
			props: { component: "span" },
			style: {
				fontSize: "inherit",
				fontWeight: "inherit",
				lineHeight: "inherit",
			},
		},
		{
			props: { variant: "body3" },
			style: ({ theme }) => ({
				display: "block",
				fontSize: theme.typography.pxToRem(12),
				lineHeight: 1.25,
			}),
		},
		{
			props: { variant: "bodyMedium" },
			style: ({ theme }) => ({
				display: "block",
				fontSize: theme.typography.pxToRem(18),
				fontWeight: "inherit",
			}),
		},
		{
			props: { variant: "bodyLarge" },
			style: ({ theme }) => ({
				display: "block",
				fontSize: theme.typography.pxToRem(20),
				fontWeight: "inherit",
			}),
		},
		{
			props: { variant: "bodyXLarge" },
			style: ({ theme }) => ({
				display: "block",
				fontSize: theme.typography.pxToRem(24),
				fontWeight: "inherit",
			}),
		},
		{
			props: { variant: "website", component: "h1" },
			style: ({ theme }) => ({
				fontWeight: 700,
				[theme.breakpoints.up("xs")]: {
					fontSize: theme.typography.pxToRem(32),
				},
				[theme.breakpoints.up("md")]: {
					fontSize: theme.typography.pxToRem(48),
				},
			}),
		},
		{
			props: { variant: "website", component: "h2" },
			style: ({ theme }) => ({
				fontWeight: 700,
				[theme.breakpoints.up("xs")]: {
					fontSize: theme.typography.pxToRem(28),
				},
				[theme.breakpoints.up("md")]: {
					fontSize: theme.typography.pxToRem(40),
				},
			}),
		},
		{
			props: { variant: "website", component: "p" },
			style: ({ theme }) => ({
				[theme.breakpoints.up("xs")]: {
					fontSize: theme.typography.pxToRem(16),
				},
				[theme.breakpoints.up("md")]: {
					fontSize: theme.typography.pxToRem(18),
				},
			}),
		},
		{
			props: { variant: "onboardingTitle1" },
			style: ({ theme }) => ({
				[theme.breakpoints.up("xs")]: {
					fontSize: theme.typography.pxToRem(28),
				},
				[theme.breakpoints.up("md")]: {
					fontSize: theme.typography.pxToRem(32),
				},
			}),
		},
		{
			props: { variant: "onboardingTitle2" },
			style: ({ theme }) => ({
				fontWeight: 600,
				fontSize: theme.typography.pxToRem(18),
			}),
		},
		{
			props: { variant: "docTitle1" },
			style: ({ theme }) => ({
				fontWeight: 600,
				[theme.breakpoints.up("xs")]: {
					fontSize: theme.typography.pxToRem(28),
				},
				[theme.breakpoints.up("md")]: {
					fontSize: theme.typography.pxToRem(32),
				},
			}),
		},
		{
			props: { variant: "docTitle2" },
			style: ({ theme }) => ({
				fontWeight: 600,
				fontSize: theme.typography.pxToRem(20),
			}),
		},
		{
			props: { variant: "docs", component: "h1" },
			style: ({ theme }) => ({
				fontWeight: 600,
				[theme.breakpoints.up("xs")]: {
					fontSize: theme.typography.pxToRem(24),
				},
				[theme.breakpoints.up("md")]: {
					fontSize: theme.typography.pxToRem(28),
				},
			}),
		},
		{
			props: { variant: "docs", component: "h2" },
			style: ({ theme }) => ({
				fontWeight: 600,
				[theme.breakpoints.up("xs")]: {
					fontSize: theme.typography.pxToRem(22),
				},
				[theme.breakpoints.up("md")]: {
					fontSize: theme.typography.pxToRem(24),
				},
			}),
		},
		{
			props: { variant: "docs", component: "h3" },
			style: ({ theme }) => ({
				fontWeight: 600,
				fontSize: theme.typography.pxToRem(18),
			}),
		},
		{
			props: { variant: "onboardingLabel" },
			style: ({ theme }) => ({
				fontWeight: 600,
				[theme.breakpoints.up("xs")]: {
					fontSize: theme.typography.pxToRem(16),
				},
				[theme.breakpoints.up("md")]: {
					fontSize: theme.typography.pxToRem(18),
				},
			}),
		},
		{
			props: { variant: "dashboard", component: "h1" },
			style: ({ theme }) => ({
				lineHeight: 1,
				[theme.breakpoints.up("xs")]: {
					fontSize: theme.typography.pxToRem(20),
				},
				[theme.breakpoints.up("md")]: {
					fontSize: theme.typography.pxToRem(22),
				},
			}),
		},
		{
			props: { variant: "dashboard", component: "h2" },
			style: ({ theme }) => ({
				lineHeight: 1,
				[theme.breakpoints.up("xs")]: {
					fontSize: theme.typography.pxToRem(18),
				},
				[theme.breakpoints.up("md")]: {
					fontSize: theme.typography.pxToRem(20),
				},
			}),
		},
		{
			props: { variant: "alertTitle" },
			style: ({ theme }) => ({
				fontWeight: 600,
				fontSize: theme.typography.pxToRem(16),
				lineHeight: 1.2,
			}),
		},
		{
			props: { variant: "card", component: "h3" },
			style: ({ theme }) => ({
				fontSize: theme.typography.pxToRem(18),
				fontWeight: 600,
				lineHeight: 1,
			}),
		},
	],
};
